import { toDay } from "./helpers";

/* The activity tab's type filter: eezee-server term-contract-logs LOG_TYPES. */
export const ACTIVITY_TYPES = [
    { value: "", label: "All changes" },
    { value: "price", label: "Price changes" },
    { value: "lines", label: "Lines added / removed / supplier" },
    { value: "details", label: "Details & dates" },
    { value: "documents", label: "Documents" }
];

const BATCH_VERBS = {
    UPLOAD: "uploaded an xlsx",
    BULK_SET_BACKING_SELLER: "set the backing supplier in bulk",
    BULK_REMOVE: "removed lines in bulk",
    BULK_ADJUST_PRICE_PCT: "adjusted contract prices by %"
};

/* one row of a batch summary: [singular, plural] */
const ACTION_NOUNS = {
    LINE_ADDED: ["line added", "lines added"],
    LINE_REMOVED: ["line removed", "lines removed"],
    LINE_RESTORED: ["line restored", "lines restored"],
    LINE_PRICE_CHANGE: ["contract price change", "contract price changes"],
    LINE_COST_PRICE_CHANGE: ["cost price change", "cost price changes"],
    LINE_BACKING_SELLER_CHANGE: ["backing supplier change", "backing supplier changes"],
    LINE_MAX_QUANTITY_CHANGE: ["max quantity change", "max quantity changes"]
};

const CONTRACT_FIELDS = {
    ENTERPRISE_CHANGE: "enterprise",
    REFERENCE_CHANGE: "reference",
    TITLE_CHANGE: "title",
    NOTES_CHANGE: "notes",
    START_DATE_CHANGE: "start date",
    END_DATE_CHANGE: "end date"
};

const DATE_ACTIONS = ["START_DATE_CHANGE", "END_DATE_CHANGE"];

/* LINE_ADDED / REMOVED / RESTORED store a JSON snapshot: { productId, price, costPrice, backingSellerName } */
const parseSnapshot = value => {
    try {
        return JSON.parse(value) || {};
    } catch (e) {
        return {};
    }
};

const shown = ({
    value,
}) => value === null || value === undefined || value === "" ? "none" : `${value}`;

/** "Title (SKU)" for a log row about a line, falling back to the snapshot's productId. */
export const productLabel = ({
    row,
}) => {
    const { product } = row;
    if (product && product.title) return product.sku ? `${product.title} (${product.sku})` : product.title;
    const id = (product && product._id) || parseSnapshot(row.toValue || row.fromValue).productId;
    return id ? `product ${id}` : "a line";
};

/** Enterprise ids logged by ENTERPRISE_CHANGE rows, to look their names up. */
export const loggedEnterpriseIds = ({
    rows,
}) => [...new Set(rows.filter(r => r.action === "ENTERPRISE_CHANGE").flatMap(r => [r.fromValue, r.toValue]).filter(Boolean))];

/** One log row as a sentence without its subject: "changed the contract price of X from A to B". */
export const rowSentence = ({
    row,
    enterpriseNames = {},
}) => {
    const { action, fromValue, toValue } = row;
    const product = () => productLabel({ row });
    if (CONTRACT_FIELDS[action]) {
        const format = DATE_ACTIONS.includes(action) ? value => value ? toDay({ date: value }) : value
            : action === "ENTERPRISE_CHANGE" ? value => enterpriseNames[value] || value
            : value => value;
        return `changed the ${CONTRACT_FIELDS[action]} from ${shown({ value: format(fromValue) })} to ${shown({ value: format(toValue) })}`;
    }
    switch (action) {
        case "CONTRACT_CREATED": return `created this term contract (${toValue})`;
        case "CONTRACT_DELETED": return "deleted this term contract";
        case "ATTACHMENT_ADDED": return `attached ${toValue}`;
        case "ATTACHMENT_REMOVED": return `removed the document ${fromValue}`;
        case "LINE_ADDED": return `added ${product()} at contract price ${shown({ value: parseSnapshot(toValue).price })}`;
        case "LINE_RESTORED": return `restored ${product()} at contract price ${shown({ value: parseSnapshot(toValue).price })}`;
        case "LINE_REMOVED": return `removed ${product()} (contract price was ${shown({ value: parseSnapshot(fromValue).price })})`;
        case "LINE_PRICE_CHANGE": return `changed the contract price of ${product()} from ${shown({ value: fromValue })} to ${shown({ value: toValue })}`;
        case "LINE_COST_PRICE_CHANGE": return `changed the cost price of ${product()} from ${shown({ value: fromValue })} to ${shown({ value: toValue })}`;
        case "LINE_BACKING_SELLER_CHANGE": return `changed the backing supplier of ${product()} from ${shown({ value: fromValue })} to ${shown({ value: toValue })}`;
        case "LINE_MAX_QUANTITY_CHANGE": return `changed the max quantity of ${product()} from ${fromValue || "no cap"} to ${toValue || "no cap"}`;
        default: return `${action} ${shown({ value: fromValue })} → ${shown({ value: toValue })}`;
    }
};

/** A grouped upload / bulk entry: "uploaded an xlsx: 38 contract price changes, 10 lines added". */
export const batchSentence = ({
    entry,
}) => {
    const counts = Object.keys(entry.summary || {}).map(action => {
        const n = entry.summary[action];
        const [one, many] = ACTION_NOUNS[action] || [action, action];
        return `${n} ${n === 1 ? one : many}`;
    });
    return `${BATCH_VERBS[entry.action] || entry.action || "made changes"}: ${counts.join(", ") || `${entry.count} changes`}`;
};

/* a grouped entry has a batchId; any other entry is a single log row */
export const entrySentence = ({
    entry,
    enterpriseNames,
}) => entry.batchId ? batchSentence({ entry }) : rowSentence({ row: entry, enterpriseNames });



// WEBPACK FOOTER //
// ./src/components/term-contract/activity-text.js